// src/utils/factureUtils.js
import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

// Variables d'environnement
const PHARMACY_NAME = process.env.REACT_APP_PHARMACY_NAME || "PHARMACIE NDIOL FOUTA";
const OWNER_NAME = process.env.REACT_APP_OWNER_NAME || "DR. AL Ousseynou KEITA";
const ADDRESS_LINE1 = process.env.REACT_APP_ADDRESS_LINE1 || "Sor Diagne Route Nationale N 2";
const ADDRESS_LINE2 = process.env.REACT_APP_ADDRESS_LINE2 || "X Médina Bois Saint-Louis - Tél: +221 33 961 08 80";
const BUSINESS_INFO = process.env.REACT_APP_BUSINESS_INFO || "NINEA:007968079 - RC: SN-STL-2020-A-1225";

// Fonction pour formater les prix
export const formatPrice = (price) => {
  if (!price && price !== 0) return '0 Fcfa';
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  const formattedPrice = numericPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedPrice} Fcfa`;
};

// Fonction pour convertir le montant en lettres
export const amountToWords = (amount) => {
  const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
  const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
  const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
  
  if (amount === 0) return 'zéro';
  if (amount > 999999) return 'Montant trop élevé';

  let words = '';

  const thousands = Math.floor(amount / 1000);
  if (thousands > 0) {
    if (thousands === 1) {
      words += 'mille ';
    } else {
      words += convertHundreds(thousands) + 'mille ';
    }
    amount %= 1000;
  }

  if (amount > 0) {
    words += convertHundreds(amount);
  }

  return words.trim() + ' Francs CFA';

  function convertHundreds(num) {
    let result = '';
    
    const hundreds = Math.floor(num / 100);
    if (hundreds > 0) {
      if (hundreds === 1) {
        result += 'cent ';
      } else {
        result += units[hundreds] + ' cent ';
      }
      num %= 100;
    }

    if (num > 0) {
      if (result !== '') result += '';
      
      if (num < 10) {
        result += units[num];
      } else if (num < 20) {
        result += teens[num - 10];
      } else {
        const ten = Math.floor(num / 10);
        const unit = num % 10;
        
        if (ten === 7 || ten === 9) {
          result += tens[ten] + '-' + teens[unit];
        } else {
          result += tens[ten];
          if (unit > 0) {
            if (ten === 8) {
              result += '-' + units[unit];
            } else {
              result += (ten === 1 ? ' et ' : '-') + units[unit];
            }
          }
        }
      }
    }

    return result;
  }
};

// Fonction pour détecter si c'est une facture unique ou un rapport
const isSingleInvoice = (venteData) => {
  if (!Array.isArray(venteData) || venteData.length === 0) return false;
  
  // Si toutes les ventes ont le même numéro de facture, c'est une facture unique
  const firstNumFact = venteData[0]?.numFactSrt;
  return venteData.every(vente => vente.numFactSrt === firstNumFact);
};

// Fonction utilitaire pour générer une facture de vente
export const factureVente = (venteData, options = {}) => {
  const {
    mode = 'download', // 'download' | 'print' | 'base64'
    fileName = `facture_${venteData[0]?.numFactSrt || 'vente'}_${Date.now()}`
  } = options;

  const doc = new jsPDF();

  // Couleurs simplifiées pour économie d'encre - tout en noir et blanc
  const blackColor = [0, 0, 0];
  const darkGray = [80, 80, 80];
  const lightGray = [240, 240, 240]; // Utilisé uniquement pour les lignes alternées

  // Déterminer le type de document
  const isSingle = isSingleInvoice(venteData);
  const documentTitle = isSingle ? 'FACTURE DE VENTE' : 'LISTE DES VENTES';
  const firstProduct = venteData[0] || {};

  // En-tête de la facture - fond blanc au lieu de couleur
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, 210, 60, 'F');

  // Titre principal - en noir
  doc.setFontSize(20);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text(documentTitle, 105, 25, { align: 'center' });

  // Informations pharmacie - en noir
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(PHARMACY_NAME, 105, 35, { align: 'center' });
  
  doc.setFontSize(8);
  doc.text(OWNER_NAME, 105, 41, { align: 'center' });
  doc.text(`${ADDRESS_LINE1} - ${ADDRESS_LINE2}`, 105, 47, { align: 'center' });
  doc.text(BUSINESS_INFO, 105, 53, { align: 'center' });

  // Ligne de séparation sous l'en-tête
  doc.setDrawColor(...blackColor);
  doc.setLineWidth(0.5);
  doc.line(15, 62, 195, 62);

  // Informations de la vente
  const dateVente = moment(firstProduct.dateInsert || new Date()).format('DD/MM/YYYY HH:mm');
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...blackColor);
  
  let startY = 70;
  
  // Informations spécifiques pour une facture unique
  if (isSingle) {
    doc.text(`N° Facture: ${firstProduct.numFactSrt || 'N/A'}`, 15, startY);
    doc.text(`Date: ${dateVente}`, 15, startY + 6);
    doc.text(`Vendeur: ${firstProduct.vendeur || 'Non spécifié'}`, 15, startY + 12);
    
    // Informations client pour facture unique
    doc.text(`Client: ${firstProduct.nom || 'Non spécifié'}`, 120, startY);
    doc.text(`Paiement: ${firstProduct.mode_payement || 'N/A'}`, 120, startY + 6);

    
    startY += 18;
  } else {
    // Informations pour rapport multiple
    doc.text(`Période: ${options.period || 'Toutes les ventes'}`, 15, startY);
    doc.text(`Date de génération: ${moment().format('DD/MM/YYYY HH:mm')}`, 15, startY + 6);
    doc.text(`Nombre de transactions: ${venteData.length}`, 15, startY + 12);
    
    startY += 18;
  }

  // Préparation des données du tableau
  const tableColumns = isSingle 
    ? ["N°", "PRODUIT", "QUANTITÉ", "PRIX UNITAIRE", "TOTAL"]
    : ["N°", "PRODUIT", "QUANTITÉ", "PRIX UNITAIRE", "TOTAL", "DATE", "VENDEUR"];

  const tableRows = venteData.map((produit, index) => {
    const baseRow = [
      index + 1,
      produit.nomProd,
      produit.qtVdu,
      formatPrice(produit.prixProdVendu),
      formatPrice((produit.prixProdVendu || 0) * (produit.qtVdu || 0))
    ];
    
    // Ajouter les colonnes supplémentaires pour les rapports
    if (!isSingle) {
      baseRow.push(
        moment(produit.dateInsert).format('DD/MM/YYYY HH:mm'),
        produit.vendeur || 'N/A'
      );
    }
    
    return baseRow;
  });

  // Calcul des totaux
  const totals = venteData.reduce((acc, produit) => {
    const quantite = produit.qtVdu || 0;
    const prix = produit.prixProdVendu || 0;
    const total = quantite * prix;
    
    return {
      totalQuantite: acc.totalQuantite + quantite,
      totalMontant: acc.totalMontant + total
    };
  }, { totalQuantite: 0, totalMontant: 0 });

  // Génération du tableau avec style économique
  doc.autoTable({
    startY: startY,
    head: [tableColumns],
    body: tableRows,
    theme: 'grid',
    styles: {
      fontSize: isSingle ? 8 : 7,
      cellPadding: 3,
      lineColor: darkGray,
      lineWidth: 0.1,
      textColor: blackColor,
    },
    headStyles: {
      fillColor: 255, // Fond blanc
      textColor: blackColor, // Texte noir
      fontStyle: 'bold',
      fontSize: isSingle ? 8 : 7,
      lineColor: blackColor,
    },
    bodyStyles: {
      textColor: blackColor, // Texte noir
    },
    alternateRowStyles: {
      fillColor: 255, // Fond blanc pour toutes les lignes (pas d'alternance de couleur)
    },
    margin: { left: 15, right: 15 },
  });

  const finalY = doc.lastAutoTable.finalY + 10;

  // Section des totaux
  doc.setFontSize(10);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'bold');
  doc.text('RÉCAPITULATIF', 15, finalY);

  doc.setFontSize(9);
  doc.setTextColor(...blackColor);
  doc.setFont('helvetica', 'normal');
  doc.text(`Nombre d'articles: ${totals.totalQuantite}`, 15, finalY + 8);
  doc.text(`Total général: ${formatPrice(totals.totalMontant)}`, 15, finalY + 16);

  // Remise si applicable (uniquement pour facture unique)
  if (isSingle && firstProduct.remise > 0) {
    const montantRemise = (firstProduct.remise * totals.totalMontant) / 100;
    const netAPayer = totals.totalMontant - montantRemise;
    
    doc.text(`Remise (${firstProduct.remise}%): -${formatPrice(montantRemise)}`, 15, finalY + 24);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...blackColor);
    doc.text(`NET À PAYER: ${formatPrice(netAPayer)}`, 15, finalY + 32);
    
    // Montant en lettres (uniquement pour facture unique)
    doc.setFontSize(8);
    doc.setTextColor(...blackColor);
    doc.setFont('helvetica', 'italic');
    doc.text(`Arrêtée à la somme de: ${amountToWords(netAPayer).toUpperCase()}`, 15, finalY + 42);
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...blackColor);
    doc.text(`TOTAL GÉNÉRAL: ${formatPrice(totals.totalMontant)}`, 15, finalY + 24);
    
    // Montant en lettres uniquement pour facture unique
    if (isSingle) {
      doc.setFontSize(8);
      doc.setTextColor(...blackColor);
      doc.setFont('helvetica', 'italic');
      doc.text(`Arrêtée à la somme de: ${amountToWords(totals.totalMontant).toUpperCase()}`, 15, finalY + 32);
    }
  }

  // Pied de page
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(7);
  doc.setTextColor(...blackColor);
  doc.text(
    `Merci pour votre confiance ! - ${PHARMACY_NAME}`,
    105,
    pageHeight - 10,
    { align: 'center' }
  );

  // Retour selon le mode
  switch (mode) {
    case 'print':
      doc.autoPrint();
      doc.output('dataurlnewwindow');
      break;
    case 'base64':
      return doc.output('datauristring');
    case 'download':
    default:
      doc.save(`${fileName}.pdf`);
      break;
  }
};

export default factureVente;